import { Box, Grid, SvgIcon, useTheme } from '@mui/material';
import {
  AttachMoney,
  Block,
  ContentCopy,
  ReceiptLong,
  RemoveShoppingCart,
  ReportProblem,
  Straighten,
  WarningAmber,
} from '@mui/icons-material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Section } from '../../../../components/ui/Section';
import { CatalogueGovernanceClient, GovernanceSummaryCounts } from '../../../../app/services/api/catalogueGovernance';

const TILE_COLORS = ['#1976d2', '#2e7d32', '#ed6c02', '#9c27b0', '#d32f2f'];

const CARD_DEFS: { key: keyof GovernanceSummaryCounts; labelKey: string; label: string; subtitleKey: string; subtitle: string; icon: JSX.Element }[] = [
  { key: 'pendingPriceChanges', labelKey: 'summaryTiles.pendingPriceChanges.label', label: 'Pending price changes', subtitleKey: 'summaryTiles.pendingPriceChanges.subtitle', subtitle: 'Submitted, not yet actioned', icon: <AttachMoney /> },
  { key: 'unavailableProducts', labelKey: 'summaryTiles.unavailableProducts.label', label: 'Unavailable products', subtitleKey: 'summaryTiles.unavailableProducts.subtitle', subtitle: 'Currently unavailable', icon: <RemoveShoppingCart /> },
  { key: 'openOffContractRequests', labelKey: 'summaryTiles.openOffContractRequests.label', label: 'Open off-contract requests', subtitleKey: 'summaryTiles.openOffContractRequests.subtitle', subtitle: 'Awaiting review', icon: <ReceiptLong /> },
  { key: 'dataQualityIssues', labelKey: 'summaryTiles.dataQualityIssues.label', label: 'Data quality issues', subtitleKey: 'summaryTiles.dataQualityIssues.subtitle', subtitle: 'Flagged for review', icon: <ReportProblem /> },
  { key: 'duplicateProductCandidates', labelKey: 'summaryTiles.duplicateProductCandidates.label', label: 'Duplicate product candidates', subtitleKey: 'summaryTiles.duplicateProductCandidates.subtitle', subtitle: 'Possible duplicates found', icon: <ContentCopy /> },
  { key: 'blockedProducts', labelKey: 'summaryTiles.blockedProducts.label', label: 'Blocked products', subtitleKey: 'summaryTiles.blockedProducts.subtitle', subtitle: 'Blocked from ordering', icon: <Block /> },
  { key: 'productsMissingAllergens', labelKey: 'summaryTiles.productsMissingAllergens.label', label: 'Products missing allergens', subtitleKey: 'summaryTiles.productsMissingAllergens.subtitle', subtitle: 'Allergen data incomplete', icon: <WarningAmber /> },
  { key: 'productsWithUomIssues', labelKey: 'summaryTiles.productsWithUomIssues.label', label: 'Products with UOM issues', subtitleKey: 'summaryTiles.productsWithUomIssues.subtitle', subtitle: 'Unit of measure problems', icon: <Straighten /> },
];

/** Governance summary tiles shown above the tabs (not just on the Overview tab) so the
 * headline counts stay visible regardless of which tab is active. Wrapped onto two rows
 * of four (md=3) rather than one row of eight. */
const SummaryTiles = () => {
  const { t } = useTranslation('catalogueGovernance');
  const theme = useTheme();
  const [counts, setCounts] = useState<GovernanceSummaryCounts | null>(null);

  useEffect(() => {
    CatalogueGovernanceClient.getSummaryCounts().then(setCounts);
  }, []);

  return (
    <Grid container
      spacing={2}
      sx={{ mt: 2, mb: 3 }}>
      {CARD_DEFS.map((card, index) => (
        <Grid item
          xs={12}
          sm={6}
          md={3}
          key={card.key}>
          <Section style={{ p: 4, mb: 0 }}>
            <Box display="flex"
              alignItems="center"
              justifyContent="space-between">
              <Box flex={1}>
                <Box sx={{ color: theme.palette.custom.gray[600], fontSize: '10px', textTransform: 'uppercase', mb: 0.25, fontWeight: 500 }}>
                  {t(card.labelKey, card.label)}
                </Box>
                <Box sx={{ fontSize: '20px', fontWeight: 600, color: theme.palette.custom.gray[900], mb: 0.25 }}>
                  {counts ? counts[card.key] : '-'}
                </Box>
                <Box sx={{ fontSize: '12px', color: theme.palette.custom.gray[600] }}>
                  {t(card.subtitleKey, card.subtitle)}
                </Box>
              </Box>
              <Box sx={{
                backgroundColor: `${TILE_COLORS[index % TILE_COLORS.length]}20`,
                borderRadius: '50%',
                width: 48,
                height: 48,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: TILE_COLORS[index % TILE_COLORS.length],
              }}>
                <SvgIcon fontSize="medium">{card.icon}</SvgIcon>
              </Box>
            </Box>
          </Section>
        </Grid>
      ))}
    </Grid>
  );
};

export default SummaryTiles;
